// src/pages/onboarding/shared/steps/StepPhone.jsx
import React from "react";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox.jsx";
import InputPhoneSADC from "@/components/auth/InputPhoneSADC";
import supabase from "@/services/supabaseClient";
import { splitE164ToCcLocal, toE164FromCcLocal } from "@/utils/phone";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

const PENDING_PHONE_CAPTURE_KEY = "afrimed_login_phone_capture_v1";

// ✅ BYPASS is allowed ONLY on localhost during DEV.
function canBypassVerification() {
  try {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "0.0.0.0" ||
      host.endsWith(".local");
    return (
      !!import.meta.env.DEV &&
      isLocal &&
      import.meta.env.VITE_BYPASS_VERIFICATION === "1"
    );
  } catch {
    return false;
  }
}
const BYPASS = canBypassVerification();

export default function StepPhone({
  step = 1,
  total = 11,
  value = "",
  onChangePhone,
  onChangeValue,
  onChangeCc,
  agreed = false,
  onChangeAgreed,
  onBack,
  onContinue, // (e164) => Promise<void> | void
  initialCc = "+27",
  contextNote = "",
  onTelemetry,
}) {
  const { t } = useLanguage();
  const [cc, setCc] = React.useState(initialCc);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const autoHandledRef = React.useRef(false);

  const pushChange = React.useCallback(
    (digitsOnly) => {
      onChangePhone?.(digitsOnly);
      onChangeValue?.(digitsOnly);
    },
    [onChangePhone, onChangeValue]
  );
  const setLocal = React.useCallback(
    (v) => pushChange(String(v || "").replace(/\D/g, "")),
    [pushChange]
  );
  const setCcAndNotify = React.useCallback(
    (nextCc) => {
      const normalized = String(nextCc || "").trim();
      if (!normalized) return;
      setCc(normalized);
      onChangeCc?.(normalized);
    },
    [onChangeCc]
  );

  const len = (value || "").length;
  const e164Current = toE164FromCcLocal(cc, value);
  const numberLooksOk = !!e164Current;

  const canContinue = (agreed || BYPASS) && (numberLooksOk || BYPASS) && !loading;

  React.useEffect(() => {
    const normalized = String(initialCc || "").trim();
    if (!normalized || normalized === cc) return;
    setCc(normalized);
  }, [initialCc, cc]);

  React.useEffect(() => {
    const parts = splitE164ToCcLocal(value);
    if (!parts?.cc || !parts?.local) return;
    if (parts.cc !== cc) setCcAndNotify(parts.cc);
    if (String(value || "") !== parts.local) setLocal(parts.local);
  }, [value, cc, setCcAndNotify, setLocal]);

  React.useEffect(() => {
    if (autoHandledRef.current) return;
    if (String(value || "").trim()) return;
    autoHandledRef.current = true;

    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const currentUserId = String(data?.user?.id || "").trim();
        if (!currentUserId) return;

        const raw = localStorage.getItem(PENDING_PHONE_CAPTURE_KEY);
        if (!raw) return;

        const parsed = JSON.parse(raw);
        const e164 = String(parsed?.e164 || "").trim();
        if (!e164.startsWith("+")) return;

        const payloadUserId = String(parsed?.userId || "").trim();
        if (payloadUserId && payloadUserId !== currentUserId) return;

        const capturedAt = Number(parsed?.capturedAt || 0);
        const ageMs = Date.now() - (Number.isFinite(capturedAt) ? capturedAt : 0);
        const maxAgeMs = payloadUserId ? 24 * 60 * 60 * 1000 : 30 * 60 * 1000;
        if (!Number.isFinite(ageMs) || ageMs > maxAgeMs) {
          try {
            localStorage.removeItem(PENDING_PHONE_CAPTURE_KEY);
          } catch {
            // ignore
          }
          return;
        }

        const parts = splitE164ToCcLocal(e164);
        const nextCc = parts?.cc || String(parsed?.cc || "").trim();
        const nextLocal =
          parts?.local || String(parsed?.local || "").replace(/\D/g, "");

        if (!nextCc || !nextLocal) return;

        setCcAndNotify(nextCc);
        setLocal(nextLocal);

        await onContinue?.(e164);

        try {
          localStorage.removeItem(PENDING_PHONE_CAPTURE_KEY);
        } catch {
          // ignore
        }
      } catch {
        // no-op: normal manual flow stays active
      }
    })();
  }, [onContinue, setCcAndNotify, setLocal, value]);

  async function handleContinue() {
    setErr("");
    onTelemetry?.("continue_clicked", { valid: canContinue, saving: loading });

    if (BYPASS) {
      const fallbackLocal = (value || "71234567").replace(/\D/g, "");
      const e164Bypass = toE164FromCcLocal(cc, fallbackLocal) || `${cc}0000000`;
      try {
        setLoading(true);
        await onContinue?.(e164Bypass);
      } catch {
        onTelemetry?.("save_failed", { reason: "phone_persistence_failed" });
        setErr(
          t(
            "onboarding.phone.saveFailed",
            "We couldn't save your phone number. Please try again."
          )
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    const e164 = e164Current;
    if (!e164) {
      onTelemetry?.("validation_blocked", { reason: "invalid_phone" });
      setErr(t("onboarding.phone.invalidPhone", "Enter a valid phone number."));
      return;
    }
    if (!agreed) {
      onTelemetry?.("validation_blocked", { reason: "terms_not_accepted" });
      setErr(
        t(
          "onboarding.phone.mustAccept",
          "Please accept Terms and Privacy Policy to continue."
        )
      );
      return;
    }

    try {
      setLoading(true);
      onTelemetry?.("save_started");
      // ✅ CAPTURE ONLY — no OTP, no provider calls
      await onContinue?.(e164);
      onTelemetry?.("save_succeeded");
    } catch {
      onTelemetry?.("save_failed", { reason: "phone_persistence_failed" });
      setErr(
        t(
          "onboarding.phone.saveFailed",
          "We couldn't save your phone number. Please try again."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">
              {t("onboarding.phone.title", "Your phone number")}
            </h2>
            {contextNote ? (
              <p className="text-body-small text-on-surface-var">{contextNote}</p>
            ) : null}
          </div>
        }
        footer={
          <Button size="lg" enterKey disabled={!canContinue} onClick={handleContinue}>
            {loading
              ? t("onboarding.phone.saving", "Saving...")
              : t("onboarding.phone.continue", "Continue")}
          </Button>
        }
      >
        <InputPhoneSADC
          value={value}
          onChange={(next) => {
            onTelemetry?.("field_changed", { field: "phone", value_recorded: false });
            setLocal(next);
          }}
          cc={cc}
          onChangeCc={(next) => {
            onTelemetry?.("field_changed", { field: "phone_country_code", value_recorded: false });
            setCcAndNotify(next);
          }}
          tone="surface"
        />

        <label className="mt-4 flex items-center gap-3">
          <Checkbox
            className="shrink-0"
            ariaLabel={t("onboarding.phone.termsAriaLabel", "Agree to the Terms of Service and Privacy Policy")}
            checked={!!agreed}
            onChange={(nextAgreed) => {
              onTelemetry?.("field_changed", { field: "terms_agreed", value_recorded: false });
              onChangeAgreed?.(nextAgreed);
            }}
          />
          <span className="text-body-medium text-on-surface-var">
            {t(
              "onboarding.phone.termsLead",
              "By checking this box, I confirm that I have read and agree to the"
            )}{" "}
            <a
              className="text-primary no-underline"
              href="/legal/terms"
              target="_blank"
              rel="noreferrer"
            >
              {t("onboarding.phone.termsLink", "Terms of Service")}
            </a>{" "}
            {t("onboarding.phone.termsJoiner", "and")}{" "}
            <a
              className="text-primary no-underline"
              href="/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              {t("onboarding.phone.privacyLink", "Privacy Policy")}
            </a>
            {t("onboarding.phone.termsEnd", ".")}
          </span>
        </label>

        {!!err && <p className="mt-2 text-sm text-[rgb(var(--afrimed-danger))]">{err}</p>}

        {!numberLooksOk && (value || "").length > 0 && !err && !BYPASS && (
          <p className="mt-2 text-sm text-[rgb(var(--afrimed-danger))]">
            {t(
              "onboarding.phone.invalidPhoneLength",
              "Please enter a valid phone number."
            )}
          </p>
        )}
      </OnboardingSection>
    </div>
  );
}
